/**
 * MatchProvider — owns the `useMatch` lifecycle so the underlying
 * MatchWsClient survives navigations across the 3 match routes
 * (/matchmaking → /match → /match/results).
 *
 * Phase 2.0 mounted `useMatch` per-route, which closed and reopened the
 * WebSocket on every navigation. Phase 2.1 lifts the hook one level up:
 * the same WS instance is reused, server state is preserved, and the
 * routes pull the live state via `useMatchContext()`.
 *
 * The provider also persists the most recent matchId to localStorage so
 * the /match/history route can fetch the latest leaderboard.
 */
import {
  createContext,
  useContext,
  useEffect,
  type PropsWithChildren,
  type ReactElement,
} from 'react'
import { useMatch, type UseMatchResult } from '../hooks/useMatch'

/** Type alias matching the spec's preferred name; equivalent to UseMatchResult. */
export type UseMatchReturn = UseMatchResult

export const LAST_MATCH_ID_KEY = 'sagwa.lastMatchId'

export const MatchContext = createContext<UseMatchReturn | null>(null)

export function MatchProvider({ children }: PropsWithChildren): ReactElement {
  const match = useMatch()

  // Persist the most recent matchId for /match/history to pick up after
  // the round has ended.
  useEffect(() => {
    if (match.endResult && match.matchId) {
      try {
        window.localStorage.setItem(LAST_MATCH_ID_KEY, match.matchId)
      } catch {
        // localStorage may be unavailable (private mode, SSR) — non-fatal.
      }
    }
  }, [match.endResult, match.matchId])

  return <MatchContext.Provider value={match}>{children}</MatchContext.Provider>
}

export function useMatchContext(): UseMatchReturn {
  const ctx = useContext(MatchContext)
  if (!ctx) {
    throw new Error('useMatchContext must be used inside <MatchProvider />')
  }
  return ctx
}
